import * as React from "react"
import { activityAttribution, activityVisit } from "../activity"
import { jsonFetch } from "../bookingApi"
import { useConfirmationPolling } from "./useConfirmationPolling"
import { randomId, randomToken } from "../identifiers"
import type { Addon, HireBookingProps, Product, Quote } from "../types"
import type { useBookingSelection } from "./useBookingSelection"
import type { useBrowserSession } from "./useBrowserSession"
import type { useCheckoutAnalytics } from "./useCheckoutAnalytics"
import type { useCheckoutLifecycle } from "./useCheckoutLifecycle"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutNavigation } from "./useCheckoutNavigation"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "setPhase" | "setBookingStatus" | "setError" | "checkoutGeneration" | "setConfirmationToken" | "setClientSecret" | "product" | "availability" | "selectedAddonIds" | "setSelectedAddonIds" | "setQuote" | "quote" | "setPostcode" | "activeSlug" | "setProduct">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl" | "calendarReady" | "calendarRangeError" | "choice" | "selection" | "dateMessage" | "meta" | "checkoutResumable">
    & Pick<ReturnType<typeof useBrowserSession>,
        "browserSessionId">
    & Pick<ReturnType<typeof useCheckoutNavigation>,
        "navigateWorkflow">
    & Pick<ReturnType<typeof useCheckoutAnalytics>,
        "analytics">
    & Pick<ReturnType<typeof useCheckoutLifecycle>,
        "invalidateCheckout">
    & Pick<ReturnType<typeof useBookingSelection>,
        "commonRequest">
    & { props: HireBookingProps }

export function useCheckoutSession(inputs: Inputs) {
    const {
        setPhase,
        baseUrl,
        setBookingStatus,
        setError,
        browserSessionId,
        checkoutGeneration,
        setConfirmationToken,
        setClientSecret,
        calendarReady,
        calendarRangeError,
        product,
        choice,
        selection,
        dateMessage,
        meta,
        availability,
        navigateWorkflow,
        analytics,
        invalidateCheckout,
        selectedAddonIds,
        setSelectedAddonIds,
        setQuote,
        quote,
        checkoutResumable,
        commonRequest,
        props,
        setPostcode,
        activeSlug,
        setProduct,
    } = inputs
    const preparingGeneration = React.useRef<number | null>(null)
    const confirmation = useConfirmationPolling(baseUrl, status => {
        setBookingStatus(status)
        setPhase("complete")
    })
    const pollStatus = React.useCallback(async (token: string) => {
        setPhase("confirming")
        setError("")
        await confirmation.check({ confirmationToken: token })
    }, [confirmation.check])

    const startCheckout = async (exactQuote: Quote, generation: number) => {
        const storageKey = `arctic-hire:checkout:${exactQuote.quoteToken}`
        let saved: { idempotencyKey: string; confirmationToken: string } | null = null
        if (typeof window !== "undefined") { try { saved = JSON.parse(window.sessionStorage.getItem(storageKey) || "null") } catch { saved = null } }
        const identifiers = saved ?? { idempotencyKey: randomId(), confirmationToken: randomToken() }
        if (typeof window !== "undefined") window.sessionStorage.setItem(storageKey, JSON.stringify(identifiers))
        const result = await jsonFetch<{ bookingReference: string; confirmationToken: string; clientSecret?: string; status: "payment_pending" | "confirmed" | "manual_review" }>(`${baseUrl}/v1/checkout-session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ quoteToken: exactQuote.quoteToken, idempotencyKey: identifiers.idempotencyKey, confirmationToken: identifiers.confirmationToken, browserSessionId, termsVersion: exactQuote.terms.version, nameCollection: "payment" }),
        })
        if (generation !== checkoutGeneration.current) {
            if (result.status === "payment_pending") {
                void jsonFetch(`${baseUrl}/v1/cancel-checkout`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ confirmationToken: result.confirmationToken }),
                }).catch(() => { /* Server-side checkout expiry remains the fallback. */ })
            }
            return
        }
        setConfirmationToken(result.confirmationToken)
        if (typeof window !== "undefined") window.sessionStorage.setItem("arctic-hire:pending-confirmation-token", result.confirmationToken)
        if (result.status !== "payment_pending") { void pollStatus(result.confirmationToken); return false }
        if (!result.clientSecret) throw new Error("Stripe did not return a payment session. Refresh and try again.")
        setClientSecret(result.clientSecret)
        return true
    }

    const continueFromSelection = async () => {
        if (!calendarReady || calendarRangeError) return
        if (!product || !choice || !selection) { setError(dateMessage); return }
        if (meta.type === "dehumidifier") return continueFromAddons()
        if (!availability?.available) return
        navigateWorkflow("addons")
        analytics("hire_addons_viewed", { available_addons: product.accessories.length })
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const toggleAddon = (addon: Addon) => {
        invalidateCheckout()
        const selected = selectedAddonIds.includes(addon.id)
        setSelectedAddonIds((current) => selected ? current.filter((id) => id !== addon.id) : [...current, addon.id])
        setQuote(null)
        setError("")
        analytics(selected ? "hire_addon_removed" : "hire_addon_added", {
            addon_slug: addon.slug,
            value: addon.pricePence / 100,
            currency: "GBP",
        })
    }

    const continueFromAddons = async () => {
        if (preparingGeneration.current === checkoutGeneration.current) return
        if (!calendarReady || calendarRangeError) return
        if (!product || !choice || !selection) { setError(dateMessage); return }
        if (!availability?.available) return
        if (quote && !checkoutResumable()) invalidateCheckout()
        if (checkoutResumable()) { navigateWorkflow("payment"); return }
        // Navigation cancels the previous generation. Capture the new one so
        // Back/Edit can still discard this preparation and cancel its session.
        navigateWorkflow("payment")
        const generation = checkoutGeneration.current
        preparingGeneration.current = generation
        setPhase("initialising")
        try {
            const exactQuote = await jsonFetch<Quote>(`${baseUrl}/v1/quote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...commonRequest, availabilityCheckKey: availability.availabilityCheckKey, accessoryIds: selectedAddonIds, attribution: activityAttribution(props.analyticsVariant), activityVisit: activityVisit() }),
            })
            if (generation !== checkoutGeneration.current) return
            setQuote(exactQuote)
            setPostcode(exactQuote.postcode)
            const pending = await startCheckout(exactQuote, generation)
            if (generation !== checkoutGeneration.current) return
            if (!pending) return
            setPhase("ready")
        } catch (reason) {
            if (generation !== checkoutGeneration.current) return
            // Return to the existing retry action, preserving entered details.
            navigateWorkflow(meta.type === "dehumidifier" ? "dates" : "addons", "replace")
            const retryGeneration = checkoutGeneration.current
            const checkoutError = reason as Error & { code?: string }
            if (checkoutError.code === "INVALID_ACCESSORIES") {
                setPhase("initialising")
                try {
                    const response = await jsonFetch<{ product: Product }>(`${baseUrl}/v1/products/${encodeURIComponent(activeSlug)}`)
                    if (checkoutGeneration.current !== retryGeneration) return
                    setProduct(response.product)
                    const availableIds = new Set(response.product.accessories.map((addon) => addon.id))
                    setSelectedAddonIds((current) => current.filter((id) => availableIds.has(id)))
                    setError("One of your add-ons is no longer available. Review your choices and continue again.")
                } catch {
                    if (checkoutGeneration.current !== retryGeneration) return
                    setError("Add-ons could not be refreshed. Try again.")
                }
            } else {
                setError(checkoutError.message || "Checkout could not be started.")
            }
            setPhase("ready")
        } finally {
            if (preparingGeneration.current === generation) preparingGeneration.current = null
        }
    }

    return {
        pollStatus,
        confirmationChecking: confirmation.checking,
        confirmationError: confirmation.error,
        refreshConfirmation: confirmation.check,
        continueFromSelection,
        toggleAddon,
        continueFromAddons,
    }
}
